import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UsersService } from '../users-service/users.service';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import { AuthService } from '../../../authentication/service/auth.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatIcon } from '@angular/material/icon';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { ChangeDetectorRef } from '@angular/core';
import {
    UserGroupDto,
    UserRolesResponse,
    UserRoleDto,
    UpdateGroupRequest
} from '../../../protos/generated/admin.pb';
import { filter, map, take } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-update-user-group',
    standalone: true,
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        MatInputModule,
        MatCheckbox,
        MatButtonModule,
        MatTreeModule,
        MatIcon,
        RouterLink,
        MatIconModule,
        MatCheckboxModule,
        TranslatePipe
    ],
    templateUrl: './update-user-group.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './update-user-group.component.scss'
})
export class UpdateUserGroupComponent implements OnInit {
    roleMap: Map<string, RoleTree> = new Map();
    editUserGroupForm!: FormGroup;
    selectedRoles: number[] = [];
    errorMessage = '';
    SucessMessage = false;
    isLoading = false;
    userGroupId!: number;
    userGroup: UserGroupDto | any;
    dataSource = new MatTreeNestedDataSource<RoleTree>();
    treeControl = new NestedTreeControl<RoleTree>(node => node.children ?? []);

    constructor(
        private fb: FormBuilder,
        private userService: UsersService,
        private authService: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.editUserGroupForm = this.fb.group({
            title: ['', Validators.required]
        });

        this.userGroupId = +this.route.snapshot.params['id'];

        this.FetchRoles()
            .then(() => {
                this.userService
                    .getAllUserGroups()
                    .pipe(
                        map((res: any): UserGroupDto | undefined => {
                            const groups: UserGroupDto[] =
                                res?.userGroups ?? [];
                            return groups.find(g => g.id === this.userGroupId);
                        }),
                        filter((g): g is UserGroupDto => !!g),
                        take(1)
                    )
                    .subscribe((userGroup: any) => {
                        this.userGroup = userGroup;
                        this.editUserGroupForm.patchValue({
                            title: userGroup.title
                        });
                        this.selectedRoles = [...(userGroup.roles || [])];
                    });
            })
            .catch(error => {
                if (error.statusCode === 16) {
                    this.authService.logOut();
                } else {
                    this.errorMessage = error.statusMessage;
                }
            });
    }

    FetchRoles(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.userService.getAllRoles().subscribe({
                next: (response: UserRolesResponse) => {
                    this.convertRolesToUserGroupsTree(
                        response?.userRoles ?? []
                    );
                    resolve();
                },
                error: (error: GrpcStatusEvent) => {
                    if (error.statusCode === 16) this.authService.logOut();
                    else this.errorMessage = error.statusMessage;
                    reject(error);
                }
            });
        });
    }

    convertRolesToUserGroupsTree(roles: UserRoleDto[] = []) {
        this.roleMap.clear();

        roles.forEach(role => {
            this.roleMap.set(role.title, new RoleTree(role.id, role.title));
        });

        roles.forEach(role => {
            const parts = (role.title ?? '').split('.');
            if (parts.length === 3) {
                const parentTitle = parts.slice(0, 2).join('.');
                const parent = this.roleMap.get(parentTitle);
                const current = this.roleMap.get(role.title);
                if (parent && current) {
                    parent.children.push(current);
                    this.roleMap.delete(role.title);
                }
            }
        });

        const roots = Array.from(this.roleMap.values());
        this.dataSource.data = roots;

        this.treeControl.dataNodes = roots;

        this.cdr.detectChanges();
    }

    toggleRoleSelection(roleId: number, checked: boolean) {
        if (checked) {
            this.selectedRoles.push(roleId);
        } else {
            this.selectedRoles = this.selectedRoles.filter(id => id !== roleId);
        }
    }

    areAllChildrenChecked(role: RoleTree): boolean {
        return (
            role.children.length > 0 &&
            role.children.every(child => this.selectedRoles.includes(child.id))
        );
    }

    areSomeChildrenRoleChecked(role: RoleTree): boolean {
        return role.children.some(child =>
            this.selectedRoles.includes(child.id)
        );
    }

    toggleAllRoles(role: RoleTree, checked: boolean) {
        if (checked) {
            role.children.forEach(child => {
                if (!this.selectedRoles.includes(child.id)) {
                    this.selectedRoles.push(child.id);
                }
            });
        } else {
            role.children.forEach(child => {
                this.selectedRoles = this.selectedRoles.filter(
                    id => id !== child.id
                );
            });
        }
    }

    onUpdateUserGroup() {
        this.isLoading = true;
        const updateGroupRequest = new UpdateGroupRequest();
        updateGroupRequest.id = this.userGroupId;
        updateGroupRequest.title = this.editUserGroupForm.get('title')?.value;
        updateGroupRequest.roles = this.selectedRoles;

        this.userService.updateUserGroup(updateGroupRequest).subscribe({
            next: () => {
                this.SucessMessage = true;
                setTimeout(() => {
                    this.router.navigate(['/users/user-groups'], {
                        queryParams: { updated: new Date().getTime() }
                    });
                }, 1000);
            },
            error: (error: GrpcStatusEvent) => {
                if (error.statusCode === 16) {
                    this.authService.logOut();
                } else {
                    this.errorMessage = error.statusMessage;
                }
                this.isLoading = false;
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }

    closeAlert() {
        this.errorMessage = '';
        this.SucessMessage = false;
    }

    hasChild = (_: number, node: RoleTree) =>
        !!node.children && node.children.length > 0;
}

export class RoleTree {
    constructor(
        public id: number,
        public title: string,
        public children: RoleTree[] = []
    ) {}
}
